import { Platform } from 'react-native'

import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import Button from 'app/components/base/Button/Button'
import Dialog from 'app/components/base/Dialog/Dialog'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import TextField from 'app/components/fields/TextField'
import useFormErrorHandler from 'app/hooks/useFormErrorHandler'
import Alert from 'app/services/Alert'
import { isErrorHandlerType } from 'app/services/ErrorHandler'
import { useUpdateReviewReplyMutation } from 'app/services/Query/user/user.mutation'
import { ReplySchema } from 'app/validation/schemas/user.schemas'

type ReplyDialogProps = {
  open: boolean
  reviewId?: string
  onClose: () => void
}

const ReplyDialog: RFC<ReplyDialogProps> = ({ open, reviewId, onClose }) => {
  const intl = useIntl()
  const methods = useForm({
    resolver: yupResolver(ReplySchema),
    mode: 'all'
  })

  const { isSubmitting, isValid } = methods.formState
  const handleFormError = useFormErrorHandler(methods.setError)

  const { mutateAsync } = useUpdateReviewReplyMutation()

  const onSubmit = async (values) => {
    try {
      if (reviewId) {
        await mutateAsync({
          review_id: reviewId,
          review: {
            reply_comment: values.comment
          }
        })

        Alert.success(
          intl.formatMessage({
            defaultMessage: 'Your comment has been successfully published'
          })
        )
        onClose()
      }
    } catch (error) {
      if (isErrorHandlerType(error)) handleFormError(error.data)
    }
  }

  return (
    <Dialog
      open={open}
      titleSx={{ alignSelf: 'center' }}
      actionsSx={{ justifyContent: 'center' }}
      dialogSx={{
        ...Platform.select({
          web: {
            '& .MuiDialog-paper': {
              py: 5,
              px: { xs: 2, sm: 11 },
              maxWidth: '600px',
              borderRadius: 2
            }
          }
        })
      }}
      title={<FormattedMessage defaultMessage={'Reply to a review'} />}
      onClose={onClose}
      actions={
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            onClick={onClose}
            variant="outlined"
            sx={{ width: 124 }}
          >
            <FormattedMessage defaultMessage="Cancel" />
          </Button>

          <Button
            size="small"
            onClick={methods.handleSubmit(onSubmit)}
            loading={isSubmitting}
            disabled={!isValid}
            sx={{ width: 135 }}
          >
            <FormattedMessage defaultMessage="Submit" />
          </Button>
        </Stack>
      }
      {...Platform.select({ native: { disableScroll: true } })}
    >
      <FormProvider {...methods}>
        <Stack spacing={3} sx={{ alignItems: 'center' }}>
          <Text sx={{ textAlign: 'center' }} color="text.third" variant="body2">
            <FormattedMessage
              defaultMessage={'You can leave a comment on a review'}
            />
          </Text>

          <TextField
            name="comment"
            label={intl.formatMessage({
              defaultMessage: 'Additional feedback'
            })}
            multiline
            formControlProps={{
              sx: { maxWidth: 331 }
            }}
            {...Platform.select({
              web: {
                rows: 3
              },
              native: {
                sx: {
                  maxHeight: 100,
                  height: '100%'
                }
              }
            })}
          />
        </Stack>
      </FormProvider>
    </Dialog>
  )
}

export default ReplyDialog
